import { useEffect, useRef, useState } from "react";

/** How an illustration on the signed-out page is shown: waiting to scroll into view, playing its entrance, or still in its final state. */
export type RevealState = "pending" | "playing" | "still";

/**
 * Plays an element's entrance once, the first time a quarter of it scrolls into view. Where nothing may move (`animate` is false) it
 * is shown still, in its final state, straight away; so it is where `IntersectionObserver` is unavailable.
 *
 * @param animate - Whether the page's animation is on.
 * @returns A ref for the element to watch, and the state to show it in.
 */
export function useReveal<T extends Element>(animate: boolean): [React.RefObject<T | null>, RevealState] {
  const ref = useRef<T>(null);
  const [revealed, setRevealed] = useState(() => typeof IntersectionObserver !== "function");

  useEffect(() => {
    const element = ref.current;
    if (revealed || !element) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setRevealed(true);
          observer.disconnect();
        }
      },
      { threshold: 0.25 },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [revealed]);

  return [ref, !animate ? "still" : revealed ? "playing" : "pending"];
}
